import React, { useState } from 'react';
import { Form, Input, Button, Spin } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import { getStrength, PASSWORD_PATTERN } from '../SignUp/authUtils';
import { changeMyPassword } from './resetPasswordApi';

interface ResetPasswordValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const FIELDS = ['currentPassword', 'newPassword', 'confirmPassword'];
const GENERIC_MESSAGE = 'Something went wrong. Please try again.';
const PASSWORD_HINT = 'Use 8+ characters with a number and a symbol';

export default function ResetPasswordIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [form] = Form.useForm<ResetPasswordValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Passwords live in component state only, never in localStorage
  const newPassword = Form.useWatch('newPassword', form);
  const strength = getStrength(newPassword);

  const signOutExpired = () => {
    window.localStorage.clear();
    navigate('/sign-in');
  };

  const onFinish = async (values: ResetPasswordValues) => {
    if (isSubmitting) return;
    if (values.newPassword === values.currentPassword) {
      form.setFields([
        {
          name: 'newPassword',
          errors: ['New password must be different from the current password'],
        },
      ]);
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await changeMyPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      dispatch(
        showSuccessMessage(res?.message ?? 'Password updated successfully')
      );
      form.resetFields();
    } catch (error: any) {
      const status: number | undefined = error?.response?.status;
      const data = error?.response?.data;
      const message: string =
        typeof data?.message === 'string' ? data.message : GENERIC_MESSAGE;
      const keys: string[] | undefined = data?.validation?.body?.keys;

      if (status === 401 || status === 404) {
        // Token Invalid / JWT errors / User not found: the session is no longer valid
        signOutExpired();
      } else if (Array.isArray(keys) && keys.length > 0) {
        const validationMessage: string =
          data?.validation?.body?.message ?? PASSWORD_HINT;
        const known = keys.filter((key) => FIELDS.includes(key));
        if (known.length > 0) {
          form.setFields(
            known.map((key) => ({
              name: key as keyof ResetPasswordValues,
              errors: [validationMessage],
            }))
          );
        } else {
          dispatch(showErrorMessage(validationMessage));
        }
      } else if (
        typeof data?.field === 'string' &&
        FIELDS.includes(String(data.field))
      ) {
        form.setFields([
          { name: data.field as keyof ResetPasswordValues, errors: [message] },
        ]);
      } else {
        dispatch(showErrorMessage(message));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="settings__card settings__card--narrow">
      <Spin spinning={isSubmitting} fullscreen />
      <p className="settings__title">Reset Password</p>
      <p className="settings__subtitle">
        Choose a strong password you haven&apos;t used before.
      </p>

      <Form
        form={form}
        layout="vertical"
        requiredMark={(label, { required }) => (
          <>
            {label}
            {required && <span className="settings__req">*</span>}
          </>
        )}
        onFinish={onFinish}
      >
        <Form.Item
          label="Current password"
          name="currentPassword"
          rules={[
            { required: true, message: 'Please enter your current password' },
          ]}
        >
          <Input.Password
            className="settings__input"
            placeholder="Enter current password"
            prefix={<LockOutlined />}
          />
        </Form.Item>

        <Form.Item
          label="New password"
          name="newPassword"
          rules={[
            { required: true, message: 'Please create a new password' },
            {
              pattern: PASSWORD_PATTERN,
              message: 'Password does not meet the requirements',
            },
          ]}
        >
          <Input.Password
            className="settings__input"
            placeholder="Create a new password"
            prefix={<LockOutlined />}
          />
        </Form.Item>

        <Form.Item
          label="Confirm new password"
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please re-enter the new password' },
            ({ getFieldValue }) => ({
              validator: async (_, value: string | undefined) => {
                if (
                  value === undefined ||
                  value === '' ||
                  getFieldValue('newPassword') === value
                ) {
                  return;
                }
                throw new Error('Passwords do not match');
              },
            }),
          ]}
        >
          <Input.Password
            className="settings__input"
            placeholder="Re-enter new password"
            prefix={<LockOutlined />}
          />
        </Form.Item>

        <div className="settings__strength">
          {[1, 2, 3, 4].map((level) => (
            <span
              key={level}
              className={`bar${strength >= level ? ` on level-${strength}` : ''}`}
            />
          ))}
        </div>
        <p className="settings__hint">
          Use 8+ characters with a number and a symbol.
        </p>

        <div className="settings__actions">
          <Button
            htmlType="submit"
            disabled={isSubmitting}
            className="settings__button settings__button--primary"
          >
            Reset password
          </Button>
        </div>
      </Form>
    </div>
  );
}
